import { Box, Button, Grid, IconButton, Slider, Switch, Typography } from '@mui/material';
import React, { useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import {
  ClearOutlined,
  DirectionsBike,
  DirectionsCarOutlined,
  PauseCircleOutlineOutlined,
  PlayCircleFilledWhiteOutlined,
} from '@mui/icons-material';
import RoutesFrequency from './RoutesFrequency';
import {
  FREQUENCY_EVERY_OTHER_WEEK,
  FREQUENCY_MONTHLY,
  FREQUENCY_ONCE,
  FREQUENCY_WEEKLY,
} from '../../../models/donationsNew';
import { dayOfTheWeekValues } from './RoutesSidePanel';
import { Colors } from '../../../assets/theme/Colors';
import NotesTextEditor from '../../../components/NotesTextEditor';

export const monthlyTypes = [
  { value: 1, label: '1st' },
  { value: 2, label: '2nd' },
  { value: 3, label: '3rd' },
  { value: 4, label: '4th' },
  { value: 5, label: '5th' },
  { value: -1, label: 'Last' },
];

const DayButton = ({ onClick, label = '', active = false }) => (
  <Button
    variant="outlined"
    size="small"
    onClick={onClick}
    sx={{
      borderRadius: 2,
      py: 1,
      px: 1,
      mr: 1,
      textTransform: 'none',
      fontWeight: 700,
      color: active ? Colors.appBar.main : 'text.primary',
      borderColor: active ? Colors.appBar.main : 'text.disabled',
      bgcolor: active ? '#fdeae5' : 'default',
      '&:hover': {
        color: Colors.appBar.main,
        borderColor: Colors.appBar.main,
        bgcolor: active ? '#fdeae5' : '#fff',
        opacity: active ? 0.7 : 1,
      },
    }}
  >
    {label}
  </Button>
);

const SectionHeader = ({ children }) => (
  <Typography variant="body2" sx={{ fontWeight: 700 }}>
    {children}
  </Typography>
);

const renderSizeIcon = (size, active) => {
  const containerStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' };
  const iconStyle = { color: Colors.primaryColor, opacity: active ? 1 : 0.7 };
  const textStyle = { fontWeight: 700, color: Colors.primaryColor, opacity: active ? 1 : 0.7 };

  if (size.name === 'Bicycle') {
    return (
      <Box sx={containerStyle}>
        <DirectionsBike sx={iconStyle} />
        <Typography variant="caption" sx={textStyle}>
          {size.name}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={containerStyle}>
      <DirectionsCarOutlined sx={iconStyle} />
      <Typography variant="caption" sx={textStyle}>
        {size.name}
      </Typography>
    </Box>
  );
};

const RouteSchedule = ({
  onFrequencyChange,
  onWeeklyDayChange,
  onMonthlyOrdinalChange,
  onMonthlyDayChange,
  onDateChange,
  onStartDateChange,
  onEndDateChange,
  onPauseDateChange,
  onResumeDateChange,
  onRescueSizeChange,
  onRescuerNotesChange,
  frequency = 0,
  weeklyDays = [],
  monthlyDays = [],
  date = null,
  startDate = null,
  endDate = null,
  pauseDate = null,
  resumeDate = null,
  rescueSizes = [],
  rescueSize = null,
  rescuerNotes = '',
}) => {
  const [pause, setPause] = useState(false);

  const sizesMarks = rescueSizes.map((size, index) => {
    const active = size.id === rescueSize.id;

    return {
      value: index,
      label: renderSizeIcon(size, active),
    };
  });

  const rescueSizeValue = rescueSizes.findIndex((size) => size.id === rescueSize.id);

  const handlePause = () => {
    if (pause) {
      onPauseDateChange(null);
      onResumeDateChange(null);
      setPause(false);
      return;
    }

    setPause(true);
  };

  return (
    <>
      <Box>
        <RoutesFrequency selectedFrequency={frequency} onFrequencyChange={onFrequencyChange} />
      </Box>

      {frequency === FREQUENCY_ONCE && (
        <Box sx={{ mt: 4 }}>
          <SectionHeader>Date</SectionHeader>
          <DatePicker
            size="small"
            minDate={moment()}
            value={date ? moment(date) : moment()}
            onChange={(momentObject) => {
              const stringDate = momentObject.format('YYYY-MM-DD');
              onDateChange(stringDate);
            }}
            format="MM/DD/YYYY"
            slotProps={{
              textField: {
                variant: 'outlined',
                size: 'small',
                placeholder: 'mm/dd/yyyy',
                sx: { mt: 1 },
              },
            }}
          />
        </Box>
      )}

      {frequency !== FREQUENCY_ONCE && (
        <Box sx={{ mt: 2 }}>
          <SectionHeader>Repeat on</SectionHeader>
          {frequency === FREQUENCY_WEEKLY && (
            <Typography variant="caption" sx={{ color: 'GrayText' }}>
              Runs on these days each week.
            </Typography>
          )}
          {frequency === FREQUENCY_EVERY_OTHER_WEEK && (
            <Typography variant="caption" sx={{ color: 'GrayText' }}>
              Runs on these days every other week.
            </Typography>
          )}
          {frequency === FREQUENCY_MONTHLY && (
            <Typography variant="caption" sx={{ color: 'GrayText' }}>
              Runs on these days every month.
            </Typography>
          )}

          {(frequency === FREQUENCY_WEEKLY || frequency === FREQUENCY_EVERY_OTHER_WEEK) && (
            <Box sx={{ mt: 1 }}>
              {dayOfTheWeekValues.map((day) => {
                const daySelected = weeklyDays.includes(day.name);

                return (
                  <DayButton
                    key={day?.name}
                    label={day?.label}
                    active={daySelected}
                    onClick={() => onWeeklyDayChange(day.name)}
                  />
                );
              })}
            </Box>
          )}

          {frequency === FREQUENCY_MONTHLY && (
            <Box sx={{ mt: 1 }}>
              {monthlyTypes.map((type) => {
                const ordinalSelected = monthlyDays.find((md) => md.ordinal === type.value);

                return (
                  <DayButton
                    key={type?.value}
                    label={type?.label}
                    active={ordinalSelected}
                    onClick={() => onMonthlyOrdinalChange(type.value)}
                  />
                );
              })}

              <Box>
                {monthlyDays.map((md) => {
                  const type = monthlyTypes.find((t) => t.value === md.ordinal);
                  const days = md.days || [];

                  return (
                    <Box sx={{ mt: 2 }} key={md.ordinal}>
                      <SectionHeader>{type?.label}:</SectionHeader>
                      {dayOfTheWeekValues.map((day) => {
                        const daySelected = days.includes(day.name);

                        return (
                          <DayButton
                            key={day?.name}
                            label={day?.label}
                            active={daySelected}
                            onClick={() => onMonthlyDayChange(md.ordinal, day.name)}
                          />
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 4 }}>
            <SectionHeader>Active dates</SectionHeader>
            <Grid container spacing={1}>
              <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'GrayText' }}>
                  Start on
                </Typography>
                <DatePicker
                  size="small"
                  minDate={moment()}
                  value={startDate ? moment(startDate) : moment()}
                  onChange={(momentObject) => {
                    const stringDate = momentObject.format('YYYY-MM-DD');
                    onStartDateChange(stringDate);
                  }}
                  format="MM/DD/YYYY"
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      size: 'small',
                      fullWidth: true,
                      placeholder: 'mm/dd/yyyy',
                      sx: { mt: 1 },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'GrayText' }}>
                  End on <span style={{ fontWeight: 500 }}>- optional</span>
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <DatePicker
                    size="small"
                    minDate={moment()}
                    value={endDate ? moment(endDate) : null}
                    onChange={(momentObject) => {
                      const stringDate = momentObject.format('YYYY-MM-DD');
                      onEndDateChange(stringDate);
                    }}
                    format="MM/DD/YYYY"
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        size: 'small',
                        fullWidth: true,
                        placeholder: 'mm/dd/yyyy',
                        sx: { mt: 1 },
                      },
                    }}
                  />
                  {endDate && (
                    <IconButton onClick={() => onEndDateChange(null)}>
                      <ClearOutlined />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mt: 4 }}>
            <SectionHeader>Pauses</SectionHeader>
            <Typography variant="caption" sx={{ color: 'GrayText' }}>
              Temporarily suspend this route without deleting it.
            </Typography>
            <Box
              onClick={handlePause}
              sx={{
                mt: 1,
                px: 2,
                py: 1.5,
                borderColor: pause ? 'text.disabled' : '#E0E0E0',
                borderWidth: '2px',
                borderStyle: 'solid',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', opacity: 0.7 }}>
                {pause ? (
                  <PlayCircleFilledWhiteOutlined fontSize="large" sx={{ color: 'GrayText' }} />
                ) : (
                  <PauseCircleOutlineOutlined fontSize="large" sx={{ color: 'GrayText' }} />
                )}
                <Box sx={{ ml: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Pause
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'GrayText' }}>
                    {pause ? 'Route will be paused at the chosen date.' : 'Route is active and running on schedule.'}
                  </Typography>
                </Box>
              </Box>
              <Switch checked={pause} />
            </Box>
            {pause && (
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'GrayText' }}>
                    Pause on <span style={{ fontWeight: 500 }}>- optional</span>
                  </Typography>
                  <DatePicker
                    size="small"
                    minDate={moment().add(1, 'day')}
                    value={pauseDate ? moment(pauseDate) : null}
                    onChange={(momentObject) => {
                      const stringDate = momentObject.format('YYYY-MM-DD');
                      onPauseDateChange(stringDate);
                    }}
                    format="MM/DD/YYYY"
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        size: 'small',
                        fullWidth: true,
                        placeholder: 'mm/dd/yyyy',
                        sx: { mt: 1 },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'GrayText' }}>
                    Resume on <span style={{ fontWeight: 500 }}>- optional</span>
                  </Typography>
                  <DatePicker
                    size="small"
                    minDate={moment().add(1, 'day')}
                    value={resumeDate ? moment(resumeDate) : null}
                    onChange={(momentObject) => {
                      const stringDate = momentObject.format('YYYY-MM-DD');
                      onResumeDateChange(stringDate);
                    }}
                    format="MM/DD/YYYY"
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        size: 'small',
                        fullWidth: true,
                        placeholder: 'mm/dd/yyyy',
                        sx: { mt: 1 },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </Box>
      )}
      <Box sx={{ mt: 4, pb: 1 }}>
        <SectionHeader>Vehicle Size</SectionHeader>
        <Box sx={{ mt: 1, px: 6 }}>
          <Slider
            onChange={(event, value) => onRescueSizeChange(rescueSizes[value])}
            value={rescueSizeValue || 0}
            min={0}
            step={1}
            max={rescueSizes.length - 1}
            marks={sizesMarks}
          />
        </Box>
      </Box>
      <Box sx={{ mt: 4 }}>
        <SectionHeader>Recuer Notes</SectionHeader>
        <Box sx={{ mt: 1 }}>
          <NotesTextEditor
            value={rescuerNotes}
            placeholder="Add notes for rescuers - parking, contacts, where to find donations, special handling..."
            onTextChange={(value) => onRescuerNotesChange(value)}
            styles={{
              width: '100%',
              '& .ql-editor': {
                wordBreak: 'break-word',
                minHeight: '100px',
              },
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default RouteSchedule;
